import { useEffect, useRef, useState } from 'react';
import type { CombatEntity, Spell } from '../../types/interfaces';
import { CommandBar } from '../CommandBar/CommandBar';
import { useTranslation } from '../../i18n/useTranslation';
import styles from './CombatHUD.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// Floating delta number (damage / heal / AP / MP change)
// ─────────────────────────────────────────────────────────────────────────────

interface FloatEntry {
  id:    number;
  value: number;
  kind:  'hp' | 'ap' | 'mp';
}

let floatSeq = 0;

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

interface CombatHUDProps {
  entity:          CombatEntity;
  spells:          Spell[];
  selectedSpellId: string | null;
  myTurn:          boolean;
  /** Token JWT — a CommandBar carrega os consumíveis da mochila. */
  token:           string;
  /** Chave de persistência do loadout da barra (por classe). */
  loadoutKey:      string;
  /** Muda a cada COMBAT_STATE — força a CommandBar a reler as quantidades. */
  refreshNonce:    number;
  onSelectSpell:   (spell: Spell | null) => void;
  onUseItem:       (itemId: string) => void;
  onEndTurn:       () => void;
  onExit:          () => void;
  toast?:          { error: (msg: string) => void };
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function CombatHUD({
  entity,
  spells,
  selectedSpellId,
  myTurn,
  token,
  loadoutKey,
  refreshNonce,
  onSelectSpell,
  onUseItem,
  onEndTurn,
  onExit,
}: CombatHUDProps) {
  const { t } = useTranslation();

  const hpPct = Math.max(0, Math.min(100, (entity.hp / entity.hp_max) * 100));

  // ── Track previous values to detect changes ──────────────────────────────
  const prevRef = useRef({ hp: entity.hp, ap: entity.ap, mp: entity.mp });

  // ── Floating delta entries ────────────────────────────────────────────────
  const [floaters, setFloaters] = useState<FloatEntry[]>([]);
  const [showShortcuts, setShowShortcuts] = useState(false);

  const pushFloat = (value: number, kind: FloatEntry['kind']) => {
    if (value === 0) return;
    const id = floatSeq++;
    setFloaters(prev => [...prev, { id, value, kind }]);
    setTimeout(() => {
      setFloaters(prev => prev.filter(f => f.id !== id));
    }, 1300);
  };

  useEffect(() => {
    const p = prevRef.current;

    const hpDiff = entity.hp - p.hp;
    const apDiff = entity.ap - p.ap;
    const mpDiff = entity.mp - p.mp;

    if (hpDiff !== 0) pushFloat(hpDiff, 'hp');
    if (apDiff !== 0) pushFloat(apDiff, 'ap');
    if (mpDiff !== 0) pushFloat(mpDiff, 'mp');

    prevRef.current = { hp: entity.hp, ap: entity.ap, mp: entity.mp };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity.hp, entity.ap, entity.mp]);

  // ── Dot animation: track which indices just changed ──────────────────────
  const prevApRef = useRef(entity.ap);
  const prevMpRef = useRef(entity.mp);
  const [changedAp, setChangedAp] = useState<Set<number>>(new Set());
  const [changedMp, setChangedMp] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (entity.ap !== prevApRef.current) {
      const lo = Math.min(entity.ap, prevApRef.current);
      const hi = Math.max(entity.ap, prevApRef.current);
      const changed = new Set(Array.from({ length: hi - lo }, (_, i) => lo + i));
      setChangedAp(changed);
      const t = setTimeout(() => setChangedAp(new Set()), 450);
      prevApRef.current = entity.ap;
      return () => clearTimeout(t);
    }
  }, [entity.ap]);

  useEffect(() => {
    if (entity.mp !== prevMpRef.current) {
      const lo = Math.min(entity.mp, prevMpRef.current);
      const hi = Math.max(entity.mp, prevMpRef.current);
      const changed = new Set(Array.from({ length: hi - lo }, (_, i) => lo + i));
      setChangedMp(changed);
      const t = setTimeout(() => setChangedMp(new Set()), 450);
      prevMpRef.current = entity.mp;
      return () => clearTimeout(t);
    }
  }, [entity.mp]);

  // ── Keyboard Shortcuts ────────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      const key = e.key.toLowerCase();

      // Sair do combate (Escape)
      if (key === 'escape') {
        if (showShortcuts) {
          setShowShortcuts(false);
          return;
        }
        if (selectedSpellId) {
          onSelectSpell(null); // Just deselect if a spell is selected
        } else {
          onExit();
        }
        return;
      }

      if (!myTurn) return;

      // Passar a jogada (Space)
      if (key === ' ' || key === 'spacebar') {
        e.preventDefault();
        onEndTurn();
        return;
      }
      // Os atalhos numéricos 1..8 dos slots são tratados pela CommandBar
      // (cada slot pode ser feitiço OU item).
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [myTurn, onEndTurn, onExit, selectedSpellId, showShortcuts]);

  return (
    <div className={styles.hud}>
      {/* ── Resources ──────────────────────────────────────────────────── */}
      <div className={styles.resources}>

        {/* HP */}
        <div className={styles.resource}>
          <span className={styles.resourceLabel}>{t('hud.hp')}</span>
          <div className={styles.barWrapper}>
            <div className={styles.barContainer}>
              <div
                className={styles.barFill}
                data-tipo="pv"
                style={{ width: `${hpPct}%` }}
              />
            </div>
            {/* Floating delta numbers */}
            {floaters.filter(f => f.kind === 'hp').map(f => (
              <span
                key={f.id}
                className={`${styles.floatDelta} ${f.value < 0 ? styles.floatNeg : styles.floatPos}`}
              >
                {f.value > 0 ? '+' : ''}{f.value}
              </span>
            ))}
          </div>
          <span className={styles.resourceValue}>
            {entity.hp} / {entity.hp_max}
          </span>
        </div>

        {/* AP */}
        <div className={styles.resource}>
          <span className={styles.resourceLabel}>{t('hud.ap')}</span>
          <div className={styles.barWrapper}>
            <div className={styles.dotsContainer} data-tipo="pa">
              {Array.from({ length: entity.ap_max }).map((_, i) => (
                <div
                  key={i}
                  className={styles.dot}
                  data-ativo={i < entity.ap ? 'true' : 'false'}
                  data-tipo="pa"
                  data-changed={changedAp.has(i) ? 'true' : 'false'}
                />
              ))}
            </div>
            {floaters.filter(f => f.kind === 'ap').map(f => (
              <span
                key={f.id}
                className={`${styles.floatDelta} ${styles.floatSmall} ${f.value < 0 ? styles.floatNeg : styles.floatPos}`}
              >
                {f.value > 0 ? '+' : ''}{f.value}
              </span>
            ))}
          </div>
          <span className={styles.resourceValue}>{entity.ap}</span>
        </div>

        {/* MP */}
        <div className={styles.resource}>
          <span className={styles.resourceLabel}>{t('hud.mp')}</span>
          <div className={styles.barWrapper}>
            <div className={styles.dotsContainer} data-tipo="pm">
              {Array.from({ length: entity.mp_max }).map((_, i) => (
                <div
                  key={i}
                  className={styles.dot}
                  data-ativo={i < entity.mp ? 'true' : 'false'}
                  data-tipo="pm"
                  data-changed={changedMp.has(i) ? 'true' : 'false'}
                />
              ))}
            </div>
            {floaters.filter(f => f.kind === 'mp').map(f => (
              <span
                key={f.id}
                className={`${styles.floatDelta} ${styles.floatSmall} ${f.value < 0 ? styles.floatNeg : styles.floatPos}`}
              >
                {f.value > 0 ? '+' : ''}{f.value}
              </span>
            ))}
          </div>
          <span className={styles.resourceValue}>{entity.mp}</span>
        </div>
      </div>

      {/* ── Identity ─────────────────────────────────────────────────────── */}
      <div className={styles.identity}>
        <span className={styles.entityName}>{entity.name}</span>
        {myTurn && <span className={styles.turnTag}>{t('hud.yourTurn')}</span>}
      </div>

      {/* ── Barra de comando (feitiços + itens, customizável) ────────────── */}
      <CommandBar
        token={token}
        loadoutKey={loadoutKey}
        spells={spells}
        selectedSpellId={selectedSpellId}
        currentAp={entity.ap}
        cooldowns={entity.cooldowns}
        active={myTurn}
        refreshNonce={refreshNonce}
        onSelectSpell={(s) => onSelectSpell(s)}
        onUseItem={onUseItem}
      />

      {/* ── Help / Shortcuts Button ── */}
      <button 
        className={styles.btnShortcuts}
        onClick={() => setShowShortcuts(true)}
        title={t('hud.shortcutsTitle')}
      >
        ⌨️
      </button>

      {/* ── End Turn ─────────────────────────────────────────────────────── */}
      <button
        className={styles.btnEndTurn}
        onClick={onEndTurn}
        disabled={!myTurn}
      >
        {t('hud.endTurn')}
        <span className={styles.btnShortcut}>[ Espaço ]</span>
      </button>

      {/* ── Modals ── */}
      {showShortcuts && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>{t('hud.shortcutsTitle')}</h3>
            <div className={styles.modalBody}>
              <ul>
                <li>
                  <span>{t('hud.shortcutSpells')}</span>
                  <span className={styles.shortcutKey}>1, 2, 3...</span>
                </li>
                <li>
                  <span>{t('hud.shortcutEndTurn')}</span>
                  <span className={styles.shortcutKey}>{t('hud.shortcutSpace')}</span>
                </li>
                <li>
                  <span>{t('hud.shortcutCancel')}</span>
                  <span className={styles.shortcutKey}>Esc</span>
                </li>
                <li>
                  <span>{t('hud.shortcutZoom')}</span>
                  <span className={styles.shortcutKey}>{t('hud.shortcutScroll')}</span>
                </li>
              </ul>
            </div>
            <div className={styles.modalActions}>
              <button className={styles.modalBtn} onClick={() => setShowShortcuts(false)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
